/**
 * Server functions for the platform admin dashboard. Server-only.
 *
 * Every call re-checks the session AND that the caller is a platform admin
 * (a route `beforeLoad` guard does NOT protect a server function). Unlike the
 * org-scoped server fns elsewhere, these intentionally read ACROSS all orgs —
 * that's the whole point of the admin view — so they must never be reachable by
 * a normal user.
 */
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { and, count, desc, eq, isNull, sql } from 'drizzle-orm'
import { z } from 'zod'
import { db } from '@/db'
import {
  account,
  invitation,
  member,
  organization,
  user as userTable,
} from '@/db/auth-schema'
import {
  emailLog,
  invoices,
  ledgerEntries,
  organizationBilling,
  prepaidTerms,
  pricingPlans,
  proxyCredentials,
} from '@/db/schema'
import {
  ADMIN_ROLE,
  type AdminPoolHealth,
  isPlatformAdmin,
  summarizePoolHealth,
  unreachablePoolHealth,
} from '@/lib/admin/admin'
import { requirePlatformAdmin } from '@/lib/admin/require-admin'
import {
  type BillingMode,
  BYTES_PER_GB,
  cycleOverageBytes,
  type FundedRail,
  gatewayOwnsSubscription,
  isEntitled,
  isWholeCentPlan,
  planPerGbMicroUsd,
  totalRemainingBytes,
  usedThisCycleBytes,
} from '@/lib/billing/billing'
import {
  gatewayForFundingSource,
  getGateway,
} from '@/lib/billing/gateways/registry'
import {
  describeOrgAcrossGateways,
  reconcileOrgAcrossGateways,
} from '@/lib/billing/gateways/sweeps.server'
import type {
  GatewayOrgDiagnostics,
  ReconcileSummary,
} from '@/lib/billing/gateways/types'
import { isGatewayId } from '@/lib/billing/gateways/types'
import { type LedgerPage, normalizeLedgerPage } from '@/lib/billing/ledger'
import { expirePlanBucket } from '@/lib/billing/plan-cycle.server'
import {
  extendPrepaidTermOnce,
  grantPrepaidCycle,
  sendPlanGrantedEmails,
} from '@/lib/billing/prepaid-terms.server'
import { getPlanForOrg, getPlansForOrg } from '@/lib/billing/pricing.server'
import {
  adjustOrgCredit,
  adjustOrgData,
} from '@/lib/billing/purchase-data.server'
import {
  deleteCredentialOnRow,
  setCredentialEnabledOnRow,
} from '@/lib/credentials/actions.server'
import {
  credentialLimit,
  MAX_CREDENTIALS_OVERRIDE,
} from '@/lib/credentials/credentials'
import { PublicError } from '@/lib/errors'
import { softDeleteOrg } from '@/lib/org/soft-delete.server'
import { liveStatusByUsername } from '@/lib/proxy-status.server'
import { auth } from '@/lib/server/auth'
import * as proxy from '@/lib/server/metered-proxy'
import { emitBusinessEvent } from '@/lib/telemetry/business.server'
import { captureMessage } from '@/lib/telemetry/telemetry'
import {
  fillDailySeries,
  normalizeRange,
  rangeDays,
  type UsageRange,
  utcDayKey,
} from '@/lib/usage/usage'
import {
  boundedOptionalString,
  displayName,
  optionalNote,
  parseInput,
  requiredText,
  id as zId,
} from '@/lib/validation'

/**
 * Admin context for the `/admin` route guard. Returns the admin user, or null
 * when the caller isn't a platform admin (the guard then redirects away).
 * Runs server-side, so it correctly honors the `ADMIN_USER_IDS` env bootstrap
 * even on client-side navigation (where process.env isn't available).
 */
export const getAdminContext = createServerFn({ method: 'GET' }).handler(
  async () => {
    const { headers } = getRequest()
    const session = await auth.api.getSession({ headers })
    if (!session || !isPlatformAdmin(session.user)) return null
    return {
      user: {
        id: session.user.id,
        name: session.user.name,
        email: session.user.email,
      },
    }
  },
)

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

export interface AdminOverview {
  userCount: number
  organizationCount: number
  credentialCount: number
  /** Sum of every org's remaining cycle allowance, in bytes. */
  totalBalanceBytes: number
  /** Orgs with an active subscription on file. */
  subscribedOrgCount: number
  /** Orgs currently over their cycle allowance (usage being written off). */
  overageOrgCount: number
}

/**
 * A client-supplied idempotency key for an admin mutation.
 *
 * Admin refs used to embed a fresh `randomUUID()` server-side, which made every
 * call unique and therefore NOT idempotent: a double-click, a React Query retry,
 * or a network blip on the response granted twice. The dialog now mints one key
 * when it opens and rotates it only after success, so a retry reuses the key and
 * the writer's `externalRef` dedup turns it into a clean no-op, while two
 * DELIBERATE identical grants carry different keys and both land.
 */
function normalizeRequestKey(raw: unknown): string {
  const key = typeof raw === 'string' ? raw.trim() : ''
  if (!/^[A-Za-z0-9_-]{8,64}$/.test(key)) {
    throw new PublicError('Missing or malformed request key', 400)
  }
  return key
}

// These two are hand-written SQL mirrors of `splitCycleUsage`. They MUST track
// it term for term: they are the numbers an operator reads to gauge outstanding
// prepaid liability, and a naive `+ credit` over-reports every org that is
// actively drawing its credit down.
const usedThisCycleSql = sql<number>`greatest(0, ${organizationBilling.groupUsedBytes} - ${organizationBilling.groupBaselineBytes})`
/** `cycleCap = max(0, allowance)` */
const cycleCapSql = sql<number>`greatest(0, ${organizationBilling.cycleAllowanceBytes})`
/** `creditCap = max(0, credit − debt)`, where `debt = max(0, −allowance)`. */
const creditCapSql = sql<number>`greatest(0, greatest(0, ${organizationBilling.creditBytes}) - greatest(0, -${organizationBilling.cycleAllowanceBytes}))`

/** Total remaining = `max(0, cycleCap + creditCap − used)`. Equivalent to
 *  `cycleRemaining + creditRemaining` because usage fills the caps in order. */
const remainingBytesSql = sql<number>`greatest(0, ${cycleCapSql} + ${creditCapSql} - ${usedThisCycleSql})`

/** Live overage: usage past BOTH caps. */
const overageBytesSql = sql<number>`greatest(0, ${usedThisCycleSql} - ${cycleCapSql} - ${creditCapSql})`

export const getAdminOverview = createServerFn({ method: 'GET' }).handler(
  async (): Promise<AdminOverview> => {
    await requirePlatformAdmin()

    const [userCount, organizationCount, credentialCount] = await Promise.all([
      db.$count(userTable),
      db.$count(organization),
      db.$count(proxyCredentials),
    ])

    const [balance] = await db
      .select({
        total: sql<number>`coalesce(sum(${remainingBytesSql}), 0)`,
        subscribed: sql<number>`count(*) filter (where ${organizationBilling.subscriptionRef} is not null)`,
        inOverage: sql<number>`count(*) filter (where ${overageBytesSql} > 0)`,
      })
      .from(organizationBilling)

    return {
      userCount,
      organizationCount,
      credentialCount,
      totalBalanceBytes: Number(balance?.total ?? 0),
      subscribedOrgCount: Number(balance?.subscribed ?? 0),
      overageOrgCount: Number(balance?.inOverage ?? 0),
    }
  },
)

// ---------------------------------------------------------------------------
// Organizations
// ---------------------------------------------------------------------------

export interface AdminOrgRow {
  id: string
  name: string
  slug: string
  createdAt: string
  /** ISO timestamp when soft-deleted, or null for live orgs. */
  deletedAt: string | null
  memberCount: number
  credentialCount: number
  balanceBytes: number
  planSlug: string | null
  /** Bytes used beyond the allowance this cycle (written off, not billed). */
  overageBytes: number
}

export const listAllOrganizations = createServerFn({ method: 'GET' }).handler(
  async (): Promise<AdminOrgRow[]> => {
    await requirePlatformAdmin()

    // Pull rows + per-org roll-ups separately and stitch in JS — avoids fanned
    // joins double-counting across the member/credential dimensions.
    const [orgs, memberCounts, credCounts, balances] = await Promise.all([
      db.select().from(organization).orderBy(desc(organization.createdAt)),
      db
        .select({ orgId: member.organizationId, n: count() })
        .from(member)
        .groupBy(member.organizationId),
      db
        .select({ orgId: proxyCredentials.organizationId, n: count() })
        .from(proxyCredentials)
        .groupBy(proxyCredentials.organizationId),
      db
        .select({
          orgId: organizationBilling.organizationId,
          balanceBytes: remainingBytesSql,
          overageBytes: overageBytesSql,
          planSlug: organizationBilling.planSlug,
        })
        .from(organizationBilling),
    ])

    const memberByOrg = new Map(memberCounts.map((r) => [r.orgId, r.n]))
    const credByOrg = new Map(credCounts.map((r) => [r.orgId, r.n]))
    const billingByOrg = new Map(balances.map((r) => [r.orgId, r]))

    return orgs.map((o) => {
      const billing = billingByOrg.get(o.id)
      return {
        id: o.id,
        name: o.name,
        slug: o.slug,
        createdAt: o.createdAt.toISOString(),
        deletedAt: o.deletedAt ? o.deletedAt.toISOString() : null,
        memberCount: memberByOrg.get(o.id) ?? 0,
        credentialCount: credByOrg.get(o.id) ?? 0,
        balanceBytes: billing?.balanceBytes ?? 0,
        planSlug: billing?.planSlug ?? null,
        overageBytes: billing?.overageBytes ?? 0,
      }
    })
  },
)

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export interface AdminUserRow {
  id: string
  name: string
  email: string
  emailVerified: boolean
  role: string | null
  banned: boolean
  createdAt: string
  /** How many organizations this user is a member of. */
  organizationCount: number
  /** Organizations this user owns, for the admin user-to-org link. */
  ownedOrgs: { id: string; name: string }[]
  /** Distinct account providerIds (`credential` = password, plus any social
   *  providers), for the sign-in-method icons. */
  providers: string[]
}

export const listAllUsers = createServerFn({ method: 'GET' }).handler(
  async (): Promise<AdminUserRow[]> => {
    await requirePlatformAdmin()

    const [users, membershipCounts, owned, accounts] = await Promise.all([
      db.select().from(userTable).orderBy(desc(userTable.createdAt)),
      db
        .select({ userId: member.userId, n: count() })
        .from(member)
        .groupBy(member.userId),
      db
        .select({
          userId: member.userId,
          orgId: organization.id,
          orgName: organization.name,
        })
        .from(member)
        .innerJoin(organization, eq(organization.id, member.organizationId))
        .where(eq(member.role, 'owner')),
      db
        .selectDistinct({
          userId: account.userId,
          providerId: account.providerId,
        })
        .from(account),
    ])

    const orgsByUser = new Map(membershipCounts.map((r) => [r.userId, r.n]))
    const ownedByUser = new Map<string, { id: string; name: string }[]>()
    for (const r of owned) {
      const list = ownedByUser.get(r.userId) ?? []
      list.push({ id: r.orgId, name: r.orgName })
      ownedByUser.set(r.userId, list)
    }
    const providersByUser = new Map<string, string[]>()
    for (const r of accounts) {
      const list = providersByUser.get(r.userId) ?? []
      list.push(r.providerId)
      providersByUser.set(r.userId, list)
    }

    return users.map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      emailVerified: u.emailVerified,
      role: u.role ?? null,
      banned: u.banned ?? false,
      createdAt: u.createdAt.toISOString(),
      organizationCount: orgsByUser.get(u.id) ?? 0,
      ownedOrgs: ownedByUser.get(u.id) ?? [],
      providers: providersByUser.get(u.id) ?? [],
    }))
  },
)

/**
 * Soft-delete an organization as a platform-admin support action. Unlike the
 * owner-facing deleteOrganization it does not require org ownership, but it
 * still refuses when the org holds a remaining cycle balance (set it to zero
 * first). Severs access but keeps billing / ledger history for the record.
 */
export const adminDeleteOrganization = createServerFn({ method: 'POST' })
  .validator((d: { organizationId: string }) =>
    parseInput(z.object({ organizationId: zId }), d),
  )
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    const [org] = await db
      .select({
        id: organization.id,
        slug: organization.slug,
        deletedAt: organization.deletedAt,
      })
      .from(organization)
      .where(eq(organization.id, data.organizationId))
      .limit(1)
    if (!org) throw new PublicError('Organization not found', 404)
    if (org.deletedAt) return { ok: true }
    await softDeleteOrg(
      { id: org.id, slug: org.slug },
      {
        balanceError:
          'This organization still has a remaining balance. Set it to zero before deleting.',
      },
    )
    emitBusinessEvent('org.delete', {
      org_id: org.id,
      slug: org.slug,
      actor: 'admin',
      admin_id: caller.userId,
    })
    return { ok: true }
  })

/**
 * Rename an organization as a platform-admin support action. Unlike the
 * owner-facing organization.update it does not require org membership, and it
 * bypasses the Better Auth org endpoints entirely, so validation is applied
 * here directly.
 */
export const adminUpdateOrgName = createServerFn({ method: 'POST' })
  .validator((d: { organizationId: string; name: string }) =>
    parseInput(z.object({ organizationId: zId, name: displayName }), d),
  )
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    const [updated] = await db
      .update(organization)
      .set({ name: data.name })
      .where(eq(organization.id, data.organizationId))
      .returning({ id: organization.id })
    if (!updated) throw new PublicError('Organization not found', 404)
    emitBusinessEvent('org.update', {
      org_id: data.organizationId,
      action: 'rename',
      name: data.name,
      actor: 'admin',
      admin_id: caller.userId,
    })
    return { ok: true }
  })

// ---------------------------------------------------------------------------
// Organization detail (members + proxy users)
// ---------------------------------------------------------------------------

export interface AdminOrgMember {
  id: string
  userId: string
  name: string
  email: string
  role: string
  createdAt: string
}

export interface AdminProxyUser {
  id: string
  username: string
  label: string | null
  /** Our DB lifecycle status (active / suspended / disabled). */
  status: 'active' | 'suspended' | 'disabled'
  /**
   * Live cumulative usage from the control plane, in bytes — the SAME number the
   * customer sees on their credential. Null when the control plane is
   * unreachable; `lastUsageBytes` is the DB fallback for that case.
   */
  usageBytes: number | null
  /** Live enabled flag from the control plane; null when unreachable. */
  enabled: boolean | null
  /** Last cumulative usage we billed against (DB high-water mark). */
  lastUsageBytes: number
  createdAt: string
}

export interface AdminOrgDetail {
  id: string
  name: string
  slug: string
  createdAt: string
  /** Remaining cycle allowance, in bytes. */
  balanceBytes: number
  /** Bytes used beyond the allowance this cycle (written off), in bytes. */
  liveOverageBytes: number
  pendingInvitationCount: number
  /** Admin override for the credential cap; null = platform default. */
  maxCredentials: number | null
  /** The cap actually enforced (override or default). */
  credentialLimit: number
  /** Admin testing switch: API requests for this org skip the rate limit. */
  apiRateLimitDisabled: boolean
  members: AdminOrgMember[]
  proxyUsers: AdminProxyUser[]
}

export const getOrgDetail = createServerFn({ method: 'GET' })
  .validator((d: { organizationId: string }) =>
    parseInput(z.object({ organizationId: zId }), d),
  )
  .handler(async ({ data }): Promise<AdminOrgDetail> => {
    await requirePlatformAdmin()
    const { organizationId } = data

    const [org] = await db
      .select()
      .from(organization)
      .where(eq(organization.id, organizationId))
      .limit(1)
    if (!org) throw new PublicError('Organization not found', 404)

    const [members, creds, balanceRow, pendingInvites] = await Promise.all([
      db
        .select({
          id: member.id,
          userId: member.userId,
          role: member.role,
          createdAt: member.createdAt,
          name: userTable.name,
          email: userTable.email,
        })
        .from(member)
        .innerJoin(userTable, eq(member.userId, userTable.id))
        .where(eq(member.organizationId, organizationId))
        .orderBy(desc(member.createdAt)),
      db
        .select()
        .from(proxyCredentials)
        .where(eq(proxyCredentials.organizationId, organizationId))
        .orderBy(desc(proxyCredentials.createdAt)),
      db
        .select({
          balanceBytes: remainingBytesSql,
          overageBytes: overageBytesSql,
          maxCredentials: organizationBilling.maxCredentials,
          apiRateLimitDisabled: organizationBilling.apiRateLimitDisabled,
        })
        .from(organizationBilling)
        .where(eq(organizationBilling.organizationId, organizationId))
        .limit(1),
      db
        .select({ n: count() })
        .from(invitation)
        .where(
          and(
            eq(invitation.organizationId, organizationId),
            eq(invitation.status, 'pending'),
          ),
        ),
    ])

    // Live usage from the control plane — best-effort, same source as the
    // customer-facing credentials view, so admin and customer agree.
    const live = await liveStatusByUsername(creds.map((c) => c.username))

    return {
      id: org.id,
      name: org.name,
      slug: org.slug,
      createdAt: org.createdAt.toISOString(),
      balanceBytes: balanceRow[0]?.balanceBytes ?? 0,
      liveOverageBytes: balanceRow[0]?.overageBytes ?? 0,
      pendingInvitationCount: pendingInvites[0]?.n ?? 0,
      maxCredentials: balanceRow[0]?.maxCredentials ?? null,
      credentialLimit: credentialLimit(balanceRow[0]?.maxCredentials),
      apiRateLimitDisabled: balanceRow[0]?.apiRateLimitDisabled ?? false,
      members: members.map((m) => ({
        id: m.id,
        userId: m.userId,
        name: m.name,
        email: m.email,
        role: m.role,
        createdAt: m.createdAt.toISOString(),
      })),
      proxyUsers: creds.map((c) => {
        const status = live.get(c.username)
        return {
          id: c.id,
          username: c.username,
          label: c.label,
          status: c.status,
          usageBytes: status?.usageBytes ?? null,
          enabled: status?.enabled ?? null,
          lastUsageBytes: c.lastUsageBytes,
          createdAt: c.createdAt.toISOString(),
        }
      }),
    }
  })

// ---------------------------------------------------------------------------
// Billing detail (per org, admin view): our record vs each gateway's
// ---------------------------------------------------------------------------

export interface AdminOrgBilling {
  // Our record (organization_billing)
  planSlug: string | null
  planName: string | null
  planIncludedGb: number | null
  subscriptionRef: string | null
  planPeriodEnd: string | null
  cycleStartAt: string | null
  cycleAllowanceBytes: number
  /** Non-expiring credit on the org. Survives cycle boundaries. */
  creditBytes: number
  usedThisCycleBytes: number
  /** Total usable now: cycle allowance left plus non-expiring credit. */
  remainingBytes: number
  overageBytes: number
  baselineSource: string
  lowBalanceNotifiedAt: string | null
  /** The two-axis truth (§1): HOW the plan renews, and whether a human runs
   *  this org's billing. */
  billingMode: BillingMode
  accountManaged: boolean
  /** Active plans, for the manual-grant picker. */
  plans: {
    slug: string
    name: string
    includedGb: number
    priceMonthlyMicroUsd: number
  }[]
  /**
   * What each payment gateway holds for this org and how it diverges from the
   * record above (live, best-effort). One entry per rail the org actually has an
   * account on, so this list is empty for an org that has only ever been
   * invoiced or comped. The admin layer stays rail-agnostic: it renders whatever
   * the gateways report rather than knowing any of their shapes.
   */
  gateways: GatewayOrgDiagnostics[]
}

export const getOrgBillingAdmin = createServerFn({ method: 'GET' })
  .validator((d: { organizationId: string }) =>
    parseInput(z.object({ organizationId: zId }), d),
  )
  .handler(async ({ data }): Promise<AdminOrgBilling> => {
    await requirePlatformAdmin()

    const [row] = await db
      .select()
      .from(organizationBilling)
      .where(eq(organizationBilling.organizationId, data.organizationId))
      .limit(1)

    // Org-aware on both counts: the picker must offer this org's own private
    // (negotiated) plans, and resolution must find a private plan rather than
    // reporting planName: null for a customer who is very much on a plan.
    const [plans, plan] = await Promise.all([
      getPlansForOrg(data.organizationId),
      getPlanForOrg(data.organizationId, row?.planSlug ?? null),
    ])

    const allowance = row?.cycleAllowanceBytes ?? 0
    const credits = row?.creditBytes ?? 0
    const used = row?.groupUsedBytes ?? 0
    const baseline = row?.groupBaselineBytes ?? 0

    return {
      planSlug: row?.planSlug ?? null,
      planName: plan?.name ?? null,
      planIncludedGb: plan?.includedGb ?? null,
      subscriptionRef: row?.subscriptionRef ?? null,
      planPeriodEnd: row?.planPeriodEnd?.toISOString() ?? null,
      cycleStartAt: row?.cycleStartAt?.toISOString() ?? null,
      cycleAllowanceBytes: allowance,
      usedThisCycleBytes: usedThisCycleBytes(used, baseline),
      remainingBytes: totalRemainingBytes(allowance, credits, used, baseline),
      creditBytes: credits,
      overageBytes: cycleOverageBytes(allowance, credits, used, baseline),
      baselineSource: row?.baselineSource ?? 'live',
      lowBalanceNotifiedAt: row?.lowBalanceNotifiedAt?.toISOString() ?? null,
      billingMode: row?.billingMode ?? 'none',
      accountManaged: row?.accountManaged ?? false,
      plans: plans.map((p) => ({
        slug: p.slug,
        name: p.name,
        includedGb: p.includedGb,
        priceMonthlyMicroUsd: p.priceMonthlyMicroUsd,
      })),
      // Each rail compares itself against our record and reports its own
      // divergences; this layer never learns any gateway's shapes.
      gateways: await describeOrgAcrossGateways({
        organizationId: data.organizationId,
        planSlug: row?.planSlug ?? null,
        subscriptionRef: row?.subscriptionRef ?? null,
        billingMode: row?.billingMode ?? 'none',
        paymentRail: row?.paymentRail ?? 'none',
        planPeriodEnd: row?.planPeriodEnd ?? null,
      }),
    }
  })

/**
 * On-demand reconcile for one org — the admin's "fix it" button. Replays any
 * missed webhooks through the same idempotent writers the webhook uses (safe to
 * run repeatedly) and reports what it healed, alarmed on, or skipped. It never
 * mutates subscriptions at Stripe; those are managed in the Stripe dashboard.
 */
export const runOrgReconcile = createServerFn({ method: 'POST' })
  .validator((d: { organizationId: string }) =>
    parseInput(z.object({ organizationId: zId }), d),
  )
  .handler(async ({ data }): Promise<ReconcileSummary> => {
    const caller = await requirePlatformAdmin()
    // Runs across every enabled gateway, so an org on a different rail is
    // reconciled by the same button. A pass with no reconcile-capable gateway
    // live comes back `skipped` rather than throwing.
    const summary = await reconcileOrgAcrossGateways(data.organizationId)
    emitBusinessEvent('org.reconcile_run', {
      org_id: data.organizationId,
      healed: summary.healed,
      alarmed: summary.alarmed,
      actor: 'admin',
      admin_id: caller.userId,
    })
    return summary
  })

// ---------------------------------------------------------------------------
// Support actions (admin mutations — every one confirmed in the UI)
// ---------------------------------------------------------------------------

/**
 * Manually add (or, negative, remove) data from an org's cycle allowance.
 * Writes through the same locked `adjustment` ledger writer as the payment
 * paths — the bug-fix lever for mis-credits and backend counter damage. The
 * admin's identity rides in the externalRef, so the admin ledger view shows
 * who did it; the description is what the CUSTOMER sees in their history.
 */
export const grantOrgData = createServerFn({ method: 'POST' })
  .validator(
    (d: {
      organizationId: string
      gb: number
      note?: string
      requestKey: string
    }) => {
      const gb = Number(d.gb)
      if (!Number.isFinite(gb) || gb === 0 || Math.abs(gb) > 10_000) {
        throw new PublicError(
          'Amount must be a non-zero number of GB (up to ±10,000)',
          400,
        )
      }
      const { organizationId, note } = parseInput(
        z.object({ organizationId: zId, note: boundedOptionalString(200) }),
        d,
      )
      return {
        organizationId,
        gb,
        note,
        requestKey: normalizeRequestKey(d.requestKey),
      }
    },
  )
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    const bytes = Math.round(data.gb * BYTES_PER_GB)
    const result = await adjustOrgData({
      organizationId: data.organizationId,
      bytes,
      externalRef: `admin-adjust:${caller.userId}:${data.requestKey}`,
      description:
        data.note ||
        (bytes > 0 ? 'Data added by support' : 'Data correction by support'),
    })
    emitBusinessEvent('add_money.admin_adjust', {
      org_id: data.organizationId,
      gb: data.gb,
      kind: 'data',
      admin_id: caller.userId,
    })
    return result
  })

/**
 * Add (or claw back) NON-EXPIRING credit on an org. Unlike `grantOrgData`, this
 * balance survives every cycle boundary — it is the enterprise/prepaid bucket,
 * spent only after the cycle allowance runs out.
 *
 * `money` is optional: pass it when the credit was bought (a wire-funded grant)
 * so it shows in revenue; omit it for goodwill credit.
 */
export const grantOrgCredit = createServerFn({ method: 'POST' })
  .validator(
    (d: {
      organizationId: string
      gb: number
      note?: string
      requestKey: string
    }) => {
      const gb = Number(d.gb)
      if (!Number.isFinite(gb) || gb === 0 || Math.abs(gb) > 1_000_000) {
        throw new PublicError(
          'Amount must be a non-zero number of GB (up to ±1,000,000)',
          400,
        )
      }
      const { organizationId, note } = parseInput(
        z.object({ organizationId: zId, note: boundedOptionalString(200) }),
        d,
      )
      return {
        organizationId,
        gb,
        note,
        requestKey: normalizeRequestKey(d.requestKey),
      }
    },
  )
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    const bytes = Math.round(data.gb * BYTES_PER_GB)
    const result = await adjustOrgCredit({
      organizationId: data.organizationId,
      bytes,
      externalRef: `admin-credit:${caller.userId}:${data.requestKey}`,
      description:
        data.note ||
        (bytes > 0
          ? 'Credit added by support'
          : 'Credit correction by support'),
    })
    emitBusinessEvent('add_money.admin_adjust', {
      org_id: data.organizationId,
      gb: data.gb,
      kind: 'credit',
      admin_id: caller.userId,
    })
    return result
  })

/**
 * Set or extend an org's PREPAID TERM on the direct rail — the ONE admin path
 * that grants a plan (§1.2, §3). A comp is this action with no invoice ever
 * issued; a paid deal is this action plus a paid invoice. Extension is the
 * §2.3 monotonic formula, so re-running never stacks. The first cycle grants
 * immediately when the org is not entitled; a live org keeps its current cycle
 * and the renewer rolls the term at the boundary.
 *
 * Refused when a GATEWAY owns a live subscription — that plan's lifecycle
 * belongs to the gateway, and a term on top would mask it.
 */
export const setOrgTerm = createServerFn({ method: 'POST' })
  .validator(
    (d: {
      organizationId: string
      planSlug: string
      months: number
      /** Override for a negotiated volume; defaults to the plan's includedGb. */
      allowanceGb?: number
      requestKey: string
    }) => {
      if (
        typeof d.planSlug !== 'string' ||
        !/^[a-z0-9-]{1,32}$/.test(d.planSlug)
      ) {
        throw new PublicError('Invalid plan', 400)
      }
      const months = Math.floor(Number(d.months))
      if (!Number.isFinite(months) || months < 1 || months > 12) {
        throw new PublicError('Term must be 1 to 12 months', 400)
      }
      const allowanceGb =
        d.allowanceGb === undefined
          ? undefined
          : Math.floor(Number(d.allowanceGb))
      if (
        allowanceGb !== undefined &&
        (!Number.isFinite(allowanceGb) || allowanceGb <= 0)
      ) {
        throw new PublicError(
          'Allowance must be a positive whole number of GB',
          400,
        )
      }
      parseInput(z.object({ organizationId: zId }), d)
      return {
        organizationId: d.organizationId,
        planSlug: d.planSlug,
        months,
        allowanceGb,
        requestKey: normalizeRequestKey(d.requestKey),
      }
    },
  )
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    const plan = await getPlanForOrg(data.organizationId, data.planSlug)
    if (!plan) throw new PublicError('Unknown plan', 404)

    const [row] = await db
      .select({
        subscriptionRef: organizationBilling.subscriptionRef,
        billingMode: organizationBilling.billingMode,
        paymentRail: organizationBilling.paymentRail,
        planPeriodEnd: organizationBilling.planPeriodEnd,
        subscriptionStatus: organizationBilling.subscriptionStatus,
      })
      .from(organizationBilling)
      .where(eq(organizationBilling.organizationId, data.organizationId))
      .limit(1)
    if (row?.subscriptionRef && gatewayOwnsSubscription(row)) {
      // A free trial is the one gateway subscription a term may replace: the
      // customer asked for an invoice deal during the trial. Cancel it at the
      // gateway first (nothing was charged); the grant below supersedes it.
      const gateway =
        row.subscriptionStatus === 'trialing'
          ? gatewayForFundingSource(row.paymentRail)
          : null
      if (!gateway?.cancelSubscription) {
        throw new PublicError(
          `This organization's plan is managed by ${row.paymentRail}. Cancel it there before putting the org on a term.`,
          400,
        )
      }
      await gateway.cancelSubscription({
        organizationId: data.organizationId,
        subscriptionRef: row.subscriptionRef,
      })
    }

    // Blank allowance keeps what the term already grants; only a brand-new
    // term defaults from the plan. Falling through to the plan default on an
    // extension would silently rewrite negotiated volumes.
    const [existingTerm] = await db
      .select({ grantBytesPerCycle: prepaidTerms.grantBytesPerCycle })
      .from(prepaidTerms)
      .where(
        and(
          eq(prepaidTerms.organizationId, data.organizationId),
          // ACTIVE only: a term ended by `removeOrgPlan` (or a lapse) must not
          // dictate the volume of the next one, or putting the org on a
          // different plan would silently re-grant the old plan's bytes.
          eq(prepaidTerms.active, true),
        ),
      )
      .limit(1)
    const grantBytes = data.allowanceGb
      ? data.allowanceGb * BYTES_PER_GB
      : (existingTerm?.grantBytesPerCycle ?? plan.includedGb * BYTES_PER_GB)

    // Term first: if the immediate grant throws, the renewal sweep still
    // picks the org up. Keyed on the requestKey via a ledger marker, so a
    // retried submit cannot stack a second extension.
    const { termEndAt, applied } = await extendPrepaidTermOnce({
      organizationId: data.organizationId,
      planSlug: plan.slug,
      months: data.months,
      grantBytesPerCycle: grantBytes,
      paymentRail: 'direct',
      createdByUserId: caller.userId,
      externalRef: `admin-term-set:${caller.userId}:${data.requestKey}`,
      description: `${data.months} month${data.months === 1 ? '' : 's'} of ${plan.name} set by support`,
    })
    if (!applied) {
      return {
        granted: false,
        planName: plan.name,
        allowanceGb: Math.round(grantBytes / BYTES_PER_GB),
        termEndAt: termEndAt.toISOString(),
      }
    }

    // Instant access for an org that is not currently entitled; a live org
    // keeps its cycle (no mid-cycle re-anchor) and rolls at the boundary.
    const entitled = isEntitled(
      {
        subscriptionRef: row?.subscriptionRef ?? null,
        planPeriodEnd: row?.planPeriodEnd ?? null,
      },
      new Date(),
    )
    let granted = false
    if (!entitled) {
      const firstEnd = new Date(
        Math.min(Date.now() + 30 * 24 * 60 * 60 * 1000, termEndAt.getTime()),
      )
      const result = await grantPrepaidCycle({
        organizationId: data.organizationId,
        planSlug: plan.slug,
        grantBytes,
        periodEnd: firstEnd,
        paymentRail: 'direct',
        externalRef: `admin-term:${caller.userId}:${data.requestKey}`,
        description: `${plan.name} plan granted by support`,
      })
      granted = result.granted
    }

    emitBusinessEvent('plan.admin_grant', {
      org_id: data.organizationId,
      plan_slug: plan.slug,
      included_gb: Math.round(grantBytes / BYTES_PER_GB),
      actor: 'admin',
      admin_id: caller.userId,
    })
    // Written confirmation for the customer (§2.10) — admin paths only, so a
    // negotiated org hears that the agreed plan landed without logging in.
    await sendPlanGrantedEmails({
      organizationId: data.organizationId,
      planName: plan.name,
      grantBytesPerCycle: grantBytes,
      termEndAt,
    })
    return {
      granted,
      planName: plan.name,
      allowanceGb: Math.round(grantBytes / BYTES_PER_GB),
      termEndAt: termEndAt.toISOString(),
    }
  })

/**
 * Gift an org extra months of its CURRENT plan — the "make it right" lever.
 *
 * Routed by billing mode, because time means different things per mode:
 *  - prepaid: extend the term by the §2.3 formula. The renewer grants the
 *    extra cycles like any paid month; a lapsed org is revived immediately.
 *    Creates the term row when a legacy comp has none — which also puts the
 *    org onto the renewal machinery for the first time.
 *  - subscription: credit the gateway customer balance with N months of the
 *    plan price. The gateway's own billing consumes the credit, the next
 *    renewal invoice collects $0, and `invoice.paid` still grants the full
 *    plan (the cycle writer deliberately grants includedGb, not cash).
 *    Nothing to schedule and no drift from the gateway's own cycle.
 */
export const giftOrgMonths = createServerFn({ method: 'POST' })
  .validator(
    (d: { organizationId: string; months: number; requestKey: string }) => {
      const months = Math.floor(Number(d.months))
      if (!Number.isFinite(months) || months < 1 || months > 12) {
        throw new PublicError('Gift must be 1 to 12 months', 400)
      }
      parseInput(z.object({ organizationId: zId }), d)
      return {
        organizationId: d.organizationId,
        months,
        requestKey: normalizeRequestKey(d.requestKey),
      }
    },
  )
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    const [row] = await db
      .select({
        planSlug: organizationBilling.planSlug,
        subscriptionRef: organizationBilling.subscriptionRef,
        billingMode: organizationBilling.billingMode,
        paymentRail: organizationBilling.paymentRail,
        planPeriodEnd: organizationBilling.planPeriodEnd,
      })
      .from(organizationBilling)
      .where(eq(organizationBilling.organizationId, data.organizationId))
      .limit(1)
    if (!row?.planSlug || row.billingMode === 'none') {
      throw new PublicError(
        'This organization has no plan to extend. Set a term first.',
        400,
      )
    }
    const plan = await getPlanForOrg(data.organizationId, row.planSlug)
    if (!plan) throw new PublicError('Unknown plan', 404)

    if (row.billingMode === 'prepaid') {
      const [term] = await db
        .select({
          grantBytesPerCycle: prepaidTerms.grantBytesPerCycle,
          paymentRail: prepaidTerms.paymentRail,
        })
        .from(prepaidTerms)
        .where(eq(prepaidTerms.organizationId, data.organizationId))
        .limit(1)
      const grantBytes =
        term?.grantBytesPerCycle ?? plan.includedGb * BYTES_PER_GB
      // An admin grant is funded in-house unless the org already has a term
      // on a real rail, in which case the grant files under that same rail.
      const stored = term?.paymentRail
      const rail: FundedRail = stored && stored !== 'none' ? stored : 'direct'
      const { termEndAt } = await extendPrepaidTermOnce({
        organizationId: data.organizationId,
        planSlug: plan.slug,
        months: data.months,
        grantBytesPerCycle: grantBytes,
        paymentRail: rail,
        createdByUserId: caller.userId,
        externalRef: `admin-gift:${caller.userId}:${data.requestKey}`,
        description: `${data.months} month${data.months === 1 ? '' : 's'} gifted by support`,
      })
      const entitled = isEntitled(
        {
          subscriptionRef: row.subscriptionRef,
          planPeriodEnd: row.planPeriodEnd,
        },
        new Date(),
      )
      if (!entitled) {
        const firstEnd = new Date(
          Math.min(Date.now() + 30 * 24 * 60 * 60 * 1000, termEndAt.getTime()),
        )
        await grantPrepaidCycle({
          organizationId: data.organizationId,
          planSlug: plan.slug,
          grantBytes,
          periodEnd: firstEnd,
          paymentRail: rail,
          externalRef: `admin-gift-grant:${caller.userId}:${data.requestKey}`,
          description: `${plan.name} plan month gifted by support`,
        })
      }
      emitBusinessEvent('plan.admin_gift', {
        org_id: data.organizationId,
        plan_slug: plan.slug,
        months: data.months,
        mode: 'prepaid',
        admin_id: caller.userId,
      })
      await sendPlanGrantedEmails({
        organizationId: data.organizationId,
        planName: plan.name,
        grantBytesPerCycle: grantBytes,
        termEndAt,
      })
      return { mode: 'prepaid' as const, termEndAt: termEndAt.toISOString() }
    }

    // Subscription: the gateway owns the schedule, so the gift is money the
    // gateway's next invoices consume — never a periodEnd we write ourselves.
    if (!isGatewayId(row.paymentRail)) {
      throw new PublicError('This subscription has no gateway to credit.', 400)
    }
    const gateway = getGateway(row.paymentRail)
    if (!gateway.creditCustomerBalance) {
      throw new PublicError(
        `${gateway.id} does not support balance credits.`,
        400,
      )
    }
    await gateway.creditCustomerBalance({
      organizationId: data.organizationId,
      amountMicroUsd: data.months * plan.priceMonthlyMicroUsd,
      description: `Gift: ${data.months} month(s) of ${plan.name}`,
      idempotencyKey: `admin-gift:${caller.userId}:${data.requestKey}`,
    })
    emitBusinessEvent('plan.admin_gift', {
      org_id: data.organizationId,
      plan_slug: plan.slug,
      months: data.months,
      mode: 'subscription',
      admin_id: caller.userId,
    })
    return { mode: 'subscription' as const, termEndAt: null }
  })

/**
 * Remove an org's plan outright — the "start over" lever, so support can put a
 * customer on a different plan instead of extending the one they are on
 * (`setOrgTerm` extends; it never replaces).
 *
 * Deactivates the prepaid term first so the renewal sweep cannot re-grant the
 * plan on the next metering tick, then runs the same `expirePlanBucket`
 * detach the cancellation webhook uses: the cycle allowance is frozen to what
 * was already used, `planSlug`/`subscriptionRef`/mode/rail are cleared, and
 * every active credential is suspended. Permanent credit survives, and so does
 * the ledger — this ends entitlement, it does not erase history.
 *
 * Refused for a gateway-owned subscription: cancelling that is the gateway's
 * job, and clearing our row alone would leave the org paying for nothing.
 */
export const removeOrgPlan = createServerFn({ method: 'POST' })
  .validator((d: { organizationId: string; requestKey: string }) => {
    parseInput(z.object({ organizationId: zId }), d)
    return {
      organizationId: d.organizationId,
      requestKey: normalizeRequestKey(d.requestKey),
    }
  })
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    const [row] = await db
      .select({
        planSlug: organizationBilling.planSlug,
        subscriptionRef: organizationBilling.subscriptionRef,
        billingMode: organizationBilling.billingMode,
        paymentRail: organizationBilling.paymentRail,
      })
      .from(organizationBilling)
      .where(eq(organizationBilling.organizationId, data.organizationId))
      .limit(1)
    if (!row || (!row.planSlug && row.billingMode === 'none')) {
      throw new PublicError('This organization has no plan to remove.', 400)
    }
    if (row.subscriptionRef && gatewayOwnsSubscription(row)) {
      throw new PublicError(
        `This organization's plan is managed by ${row.paymentRail}. Cancel it there first.`,
        400,
      )
    }

    // Before the detach, not after: a metering tick landing in between would
    // otherwise see a null planPeriodEnd on a still-active term and re-grant
    // the plan we are removing.
    await db
      .update(prepaidTerms)
      .set({ active: false, updatedAt: new Date() })
      .where(
        and(
          eq(prepaidTerms.organizationId, data.organizationId),
          eq(prepaidTerms.active, true),
        ),
      )

    const result = await expirePlanBucket({
      organizationId: data.organizationId,
      externalRef: `admin-plan-removed:${caller.userId}:${data.requestKey}`,
      description: 'Plan removed by support',
      detachSubscription: true,
    })

    emitBusinessEvent('plan.admin_removed', {
      org_id: data.organizationId,
      plan_slug: row.planSlug ?? undefined,
      billing_mode: row.billingMode,
      expired_bytes: result.expiredBytes,
      suspended: result.suspended.length,
      actor: 'admin',
      admin_id: caller.userId,
    })
    return {
      planSlug: row.planSlug,
      expiredBytes: result.expiredBytes,
      suspended: result.suspended.length,
    }
  })

/**
 * Grant or revoke the platform-admin role. Self-demotion is refused so the
 * last admin can't lock themselves out mid-session (the `ADMIN_USER_IDS` env
 * bootstrap remains the recovery path regardless).
 */
export const setUserPlatformRole = createServerFn({ method: 'POST' })
  .validator((d: { userId: string; admin: boolean }) => {
    const { userId } = parseInput(z.object({ userId: zId }), d)
    return { userId, admin: !!d.admin }
  })
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    if (!data.admin && data.userId === caller.userId) {
      throw new PublicError('You cannot remove your own admin access', 400)
    }
    const [updated] = await db
      .update(userTable)
      .set({ role: data.admin ? ADMIN_ROLE : null, updatedAt: new Date() })
      .where(eq(userTable.id, data.userId))
      .returning({ id: userTable.id })
    if (!updated) throw new PublicError('User not found', 404)
    emitBusinessEvent('account.admin_role_change', {
      user_id: data.userId,
      role: data.admin ? ADMIN_ROLE : 'none',
      actor: 'admin',
      admin_id: caller.userId,
    })
    return { ok: true }
  })

/**
 * Ban or unban a user across the whole platform. Goes through the Better Auth
 * admin plugin (not a raw column write) so it also revokes the user's active
 * sessions and honors `banExpires`. Self-ban is refused so an admin can't lock
 * themselves out mid-session.
 */
export const setUserBanned = createServerFn({ method: 'POST' })
  .validator((d: { userId: string; banned: boolean; reason?: string }) =>
    parseInput(
      z.object({ userId: zId, banned: z.boolean(), reason: optionalNote(200) }),
      d,
    ),
  )
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    if (data.banned && data.userId === caller.userId) {
      throw new PublicError('You cannot ban your own account', 400)
    }
    const { headers } = getRequest()
    try {
      if (data.banned) {
        await auth.api.banUser({
          headers,
          body: {
            userId: data.userId,
            ...(data.reason ? { banReason: data.reason } : {}),
          },
        })
      } else {
        await auth.api.unbanUser({ headers, body: { userId: data.userId } })
      }
    } catch {
      throw new PublicError(
        'Could not update the ban status for this user',
        400,
      )
    }
    emitBusinessEvent('account.admin_ban', {
      user_id: data.userId,
      banned: data.banned,
      actor: 'admin',
      admin_id: caller.userId,
    })
    captureMessage(`admin ${data.banned ? 'banned' : 'unbanned'} user`, {
      level: 'info',
      extra: {
        adminUserId: caller.userId,
        action: data.banned ? 'user.ban' : 'user.unban',
        targetUserId: data.userId,
      },
    })
    return { ok: true }
  })

// ---------------------------------------------------------------------------
// Organization member + credential support actions
// ---------------------------------------------------------------------------

/** Load a credential by id WITHOUT org scoping — admin acts across orgs. */
async function loadCredentialForAdmin(id: string) {
  const [row] = await db
    .select()
    .from(proxyCredentials)
    .where(eq(proxyCredentials.id, id))
    .limit(1)
  if (!row) throw new PublicError('Credential not found', 404)
  return row
}

/**
 * Enable or disable any org's credential (support kill-switch for a leaked or
 * abusive login). Re-enabling honors the same entitlement + balance gate as the
 * customer path; disabling is always allowed.
 */
export const adminSetCredentialEnabled = createServerFn({ method: 'POST' })
  .validator((d: { id: string; enabled: boolean }) =>
    parseInput(z.object({ id: zId, enabled: z.boolean() }), d),
  )
  .handler(async ({ data }) => {
    const { userId } = await requirePlatformAdmin()
    const row = await loadCredentialForAdmin(data.id)
    const result = await setCredentialEnabledOnRow(row, data.enabled)
    emitBusinessEvent('credential.update', {
      org_id: row.organizationId,
      credential_id: row.id,
      action: data.enabled ? 'enable' : 'disable',
      actor: 'admin',
      admin_id: userId,
    })
    captureMessage(
      `admin ${data.enabled ? 'enabled' : 'disabled'} credential ${row.username}`,
      {
        level: 'info',
        extra: {
          adminUserId: userId,
          action: data.enabled ? 'credential.enable' : 'credential.disable',
          credentialId: row.id,
          organizationId: row.organizationId,
        },
      },
    )
    return result
  })

/** Delete any org's credential (destroys the backend user, unrecoverably). */
export const adminDeleteCredential = createServerFn({ method: 'POST' })
  .validator((d: { id: string }) => parseInput(z.object({ id: zId }), d))
  .handler(async ({ data }) => {
    const { userId } = await requirePlatformAdmin()
    const row = await loadCredentialForAdmin(data.id)
    const result = await deleteCredentialOnRow(row)
    emitBusinessEvent('credential.delete', {
      org_id: row.organizationId,
      credential_id: row.id,
      actor: 'admin',
      admin_id: userId,
    })
    captureMessage(`admin deleted credential ${row.username}`, {
      level: 'info',
      extra: {
        adminUserId: userId,
        action: 'credential.delete',
        credentialId: row.id,
        organizationId: row.organizationId,
      },
    })
    return result
  })

/** Load a membership by id, asserting it belongs to the given org. */
async function loadOrgMemberForAdmin(memberId: string, organizationId: string) {
  const [row] = await db
    .select()
    .from(member)
    .where(
      and(eq(member.id, memberId), eq(member.organizationId, organizationId)),
    )
    .limit(1)
  if (!row) throw new PublicError('Member not found', 404)
  return row
}

/** How many owners the org has — used to refuse orphaning it. */
async function orgOwnerCount(organizationId: string): Promise<number> {
  const [row] = await db
    .select({ n: count() })
    .from(member)
    .where(
      and(eq(member.organizationId, organizationId), eq(member.role, 'owner')),
    )
  return row?.n ?? 0
}

/**
 * Change a member's org role on the customer's behalf. Writes the `member` row
 * directly — the Better Auth org API is scoped to a member of the target org,
 * which a platform admin isn't. Refuses to demote the last owner (that would
 * leave the org with no one who can manage billing or delete it).
 */
export const adminSetOrgMemberRole = createServerFn({ method: 'POST' })
  .validator((d: { organizationId: string; memberId: string; role: string }) =>
    parseInput(
      z.object({
        organizationId: zId,
        memberId: zId,
        role: z.enum(['owner', 'admin', 'member']),
      }),
      d,
    ),
  )
  .handler(async ({ data }) => {
    const { userId } = await requirePlatformAdmin()
    const m = await loadOrgMemberForAdmin(data.memberId, data.organizationId)
    if (
      m.role === 'owner' &&
      data.role !== 'owner' &&
      (await orgOwnerCount(data.organizationId)) <= 1
    ) {
      throw new PublicError(
        'This is the only owner. Promote another member to owner first.',
        400,
      )
    }
    await db.update(member).set({ role: data.role }).where(eq(member.id, m.id))
    emitBusinessEvent('org.member_role_change', {
      org_id: data.organizationId,
      user_id: m.userId,
      role: data.role,
      previous_role: m.role,
      actor: 'admin',
      admin_id: userId,
    })
    captureMessage(`admin changed member role ${m.role} -> ${data.role}`, {
      level: 'info',
      extra: {
        adminUserId: userId,
        action: 'member.role',
        organizationId: data.organizationId,
        memberId: m.id,
        targetUserId: m.userId,
        fromRole: m.role,
        toRole: data.role,
      },
    })
    return { ok: true }
  })

/**
 * Remove a member from an org on the customer's behalf. Direct `member` delete
 * for the same reason as the role change. Refuses to remove the last owner.
 */
export const adminRemoveOrgMember = createServerFn({ method: 'POST' })
  .validator((d: { organizationId: string; memberId: string }) =>
    parseInput(z.object({ organizationId: zId, memberId: zId }), d),
  )
  .handler(async ({ data }) => {
    const { userId } = await requirePlatformAdmin()
    const m = await loadOrgMemberForAdmin(data.memberId, data.organizationId)
    if (m.role === 'owner' && (await orgOwnerCount(data.organizationId)) <= 1) {
      throw new PublicError(
        'This is the only owner. Promote another member to owner before removing them.',
        400,
      )
    }
    await db.delete(member).where(eq(member.id, m.id))
    emitBusinessEvent('org.member_remove', {
      org_id: data.organizationId,
      user_id: m.userId,
      role: m.role,
      actor: 'admin',
      admin_id: userId,
    })
    captureMessage('admin removed org member', {
      level: 'info',
      extra: {
        adminUserId: userId,
        action: 'member.remove',
        organizationId: data.organizationId,
        memberId: m.id,
        targetUserId: m.userId,
        role: m.role,
      },
    })
    return { ok: true }
  })

// ---------------------------------------------------------------------------
// Transaction history (per org, admin view)
// ---------------------------------------------------------------------------

/** One page of the ledger for any organization (platform-admin view). */
export const getOrgLedgerAdmin = createServerFn({ method: 'GET' })
  .validator(
    (d: { organizationId: string; limit?: number; offset?: number }) => {
      const { organizationId } = parseInput(
        z.object({ organizationId: zId }),
        d,
      )
      return { organizationId, ...normalizeLedgerPage(d) }
    },
  )
  .handler(async ({ data }): Promise<LedgerPage> => {
    await requirePlatformAdmin()
    const { organizationId, limit, offset } = data
    const where = eq(ledgerEntries.organizationId, organizationId)
    // Pin the still-open daily usage bucket (today's UTC day) to the very top,
    // ahead of newer purchases — across every page, not just within one.
    const openFirst = sql`case when ${ledgerEntries.kind} = 'usage' and ${ledgerEntries.periodKey} = ${utcDayKey(new Date())} then 0 else 1 end`

    const [rows, [totals]] = await Promise.all([
      db
        .select({
          id: ledgerEntries.id,
          kind: ledgerEntries.kind,
          amountBytes: ledgerEntries.amountBytes,
          balanceAfterBytes: ledgerEntries.balanceAfterBytes,
          provider: ledgerEntries.provider,
          currency: ledgerEntries.currency,
          paidAmountMicros: ledgerEntries.paidAmountMicros,
          description: ledgerEntries.description,
          periodKey: ledgerEntries.periodKey,
          externalRef: ledgerEntries.externalRef,
          createdAt: ledgerEntries.createdAt,
        })
        .from(ledgerEntries)
        .where(where)
        .orderBy(openFirst, desc(ledgerEntries.createdAt))
        .limit(limit)
        .offset(offset),
      db.select({ value: count() }).from(ledgerEntries).where(where),
    ])

    return {
      rows: rows.map((r) => ({ ...r, createdAt: r.createdAt.toISOString() })),
      total: Number(totals?.value ?? 0),
    }
  })

// ---------------------------------------------------------------------------
// Revenue (platform-wide, admin view)
// ---------------------------------------------------------------------------

export interface RevenueOverview {
  /** All-time revenue (data purchases, invoice payments, gateway plan grants), in micro-USD. */
  totalRevenueMicros: number
  /** Revenue in the last 30 days, in micro-USD. */
  last30dRevenueMicros: number
  /** Number of money-bearing transactions, all time. */
  purchaseCount: number
  /** Total data capacity those transactions funded, in bytes. */
  totalBytesSold: number
}

/** Ledger rows that count as revenue: data purchases, offline invoice payments,
 *  and plan grants a gateway collected cash for. Comp/invoiced-term grants carry
 *  no money and are excluded; their invoice payment is the revenue row. */
const revenueRowFilter = sql`(${ledgerEntries.kind} in ('purchaseData', 'offlinePayment')
  or (${ledgerEntries.kind} = 'planGrant' and ${ledgerEntries.paidAmountMicros} is not null))`

export const getRevenueOverview = createServerFn({ method: 'GET' }).handler(
  async (): Promise<RevenueOverview> => {
    await requirePlatformAdmin()

    const [row] = await db
      .select({
        total: sql<number>`coalesce(sum(${ledgerEntries.paidAmountMicros}), 0)`,
        last30d: sql<number>`coalesce(sum(${ledgerEntries.paidAmountMicros}) filter (where ${ledgerEntries.createdAt} >= now() - interval '30 days'), 0)`,
        purchaseCount: sql<number>`count(*)`,
        bytesSold: sql<number>`coalesce(sum(${ledgerEntries.amountBytes}), 0)`,
      })
      .from(ledgerEntries)
      .where(revenueRowFilter)

    return {
      totalRevenueMicros: Number(row?.total ?? 0),
      last30dRevenueMicros: Number(row?.last30d ?? 0),
      purchaseCount: Number(row?.purchaseCount ?? 0),
      totalBytesSold: Number(row?.bytesSold ?? 0),
    }
  },
)

/** One day of revenue. `date` is a UTC `YYYY-MM-DD` key; `micros` is micro-USD. */
export interface RevenuePoint {
  date: string
  micros: number
}

/** Daily revenue series (gap-filled), platform-wide. Same row set as the overview. */
export const getRevenueSeries = createServerFn({ method: 'GET' })
  .validator((d: { range: UsageRange }) => ({ range: normalizeRange(d.range) }))
  .handler(async ({ data }): Promise<RevenuePoint[]> => {
    await requirePlatformAdmin()

    const days = rangeDays(data.range)
    const sinceFilter = days
      ? sql`AND created_at >= now() - (${sql.raw(String(days))} || ' days')::interval`
      : sql``

    const rows = await db.execute<{ day: string; micros: string }>(sql`
      SELECT
        to_char(date_trunc('day', created_at AT TIME ZONE 'UTC'), 'YYYY-MM-DD') AS day,
        coalesce(sum(paid_amount_micros), 0) AS micros
      FROM ledger_entries
      WHERE (kind in ('purchaseData', 'offlinePayment')
        or (kind = 'planGrant' and paid_amount_micros is not null)) ${sinceFilter}
      GROUP BY day
      ORDER BY day
    `)

    // Reuse the gap-fill helper (keyed on a numeric value) by carrying micros in
    // its `bytes` slot, then relabel — keeps the chart line continuous.
    const filled = fillDailySeries(
      [...rows].map((r) => ({ date: r.day, bytes: Number(r.micros) })),
      data.range,
      new Date(),
    )
    return filled.map((p) => ({ date: p.date, micros: p.bytes }))
  })

// ---------------------------------------------------------------------------
// Proxy pool health (control plane)
// ---------------------------------------------------------------------------

/**
 * Upstream proxy pool status for the admin pool-health page: per-proxy rows
 * (from `/pool`) plus the aggregate up/down counts (from `/pool/health`).
 * Best-effort — if the control plane can't be reached, returns a structured
 * "unreachable" result instead of throwing, so the page still renders (and the
 * control plane's URL/error detail never reaches the client; see AGENTS.md).
 */
export const getPoolHealthOverview = createServerFn({ method: 'GET' }).handler(
  async (): Promise<AdminPoolHealth> => {
    await requirePlatformAdmin()

    const [poolResult, healthResult] = await Promise.allSettled([
      proxy.getPool(),
      proxy.getPoolHealth(),
    ])

    if (
      poolResult.status === 'rejected' ||
      healthResult.status === 'rejected'
    ) {
      if (poolResult.status === 'rejected') {
        console.error('[admin] pool listing unreachable:', poolResult.reason)
      }
      if (healthResult.status === 'rejected') {
        console.error(
          '[admin] pool health aggregate unreachable:',
          healthResult.reason,
        )
      }
      return unreachablePoolHealth()
    }

    return summarizePoolHealth(poolResult.value, healthResult.value)
  },
)

// ---------------------------------------------------------------------------
// Enterprise / invoiced deals
// ---------------------------------------------------------------------------

/**
 * Create (or re-price) a PRIVATE plan for one org: a negotiated allowance and
 * price visible only to that org. Never appears on the public pricing page, in
 * another org's picker, or in the derived global top-up rate — `getActivePlans`
 * filters `organizationId IS NULL`, so exclusion is structural.
 *
 * Re-pricing INSERTS a new effective-dated revision rather than editing the old
 * row: past purchases price against the row that was in force when they settled.
 */
/**
 * Mark an org as account-managed BEFORE any billing exists. Managed = every
 * self-serve money surface is off (subscribe, add data) and the customer is
 * pointed at their account team — so a custom plan created during a
 * negotiation cannot be card-subscribed by the customer mid-deal. Direct
 * grants set this automatically; this fn is the explicit switch for the
 * window before the first grant, and the way back to self-serve after churn.
 */
export const setOrgAccountManaged = createServerFn({ method: 'POST' })
  .validator((d: { organizationId: string; managed: boolean }) => {
    const { organizationId } = parseInput(z.object({ organizationId: zId }), d)
    return { organizationId, managed: !!d.managed }
  })
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    await db
      .insert(organizationBilling)
      .values({
        organizationId: data.organizationId,
        accountManaged: data.managed,
      })
      .onConflictDoUpdate({
        target: organizationBilling.organizationId,
        set: { accountManaged: data.managed, updatedAt: new Date() },
      })
    emitBusinessEvent('org.account_managed_set', {
      org_id: data.organizationId,
      managed: data.managed,
      admin_id: caller.userId,
    })
    return { managed: data.managed }
  })

/**
 * Turn the customer API's per-key rate limit off (or back on) for requests
 * scoped to this org. A testing switch, so load tests aren't throttled.
 */
export const setOrgApiRateLimitDisabled = createServerFn({ method: 'POST' })
  .validator((d: { organizationId: string; disabled: boolean }) => {
    const { organizationId } = parseInput(z.object({ organizationId: zId }), d)
    return { organizationId, disabled: !!d.disabled }
  })
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    await db
      .insert(organizationBilling)
      .values({
        organizationId: data.organizationId,
        apiRateLimitDisabled: data.disabled,
      })
      .onConflictDoUpdate({
        target: organizationBilling.organizationId,
        set: { apiRateLimitDisabled: data.disabled, updatedAt: new Date() },
      })
    emitBusinessEvent('org.api_rate_limit_disabled_set', {
      org_id: data.organizationId,
      disabled: data.disabled,
      admin_id: caller.userId,
    })
    return { disabled: data.disabled }
  })

/**
 * Set (or clear, with null) the org's proxy credential cap. Lowering it below
 * the current count only blocks new creates; existing credentials are kept.
 */
export const setOrgCredentialLimit = createServerFn({ method: 'POST' })
  .validator((d: { organizationId: string; maxCredentials: number | null }) =>
    parseInput(
      z.object({
        organizationId: zId,
        maxCredentials: z
          .number()
          .int('Enter a whole number.')
          .min(1, 'The limit must be at least 1.')
          .max(
            MAX_CREDENTIALS_OVERRIDE,
            `The limit can be at most ${MAX_CREDENTIALS_OVERRIDE.toLocaleString('en-US')}.`,
          )
          .nullable(),
      }),
      d,
    ),
  )
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    await db
      .insert(organizationBilling)
      .values({
        organizationId: data.organizationId,
        maxCredentials: data.maxCredentials,
      })
      .onConflictDoUpdate({
        target: organizationBilling.organizationId,
        set: { maxCredentials: data.maxCredentials, updatedAt: new Date() },
      })
    emitBusinessEvent('org.credential_limit_set', {
      org_id: data.organizationId,
      max_credentials: data.maxCredentials,
      admin_id: caller.userId,
    })
    return { maxCredentials: data.maxCredentials }
  })

/** The org's PRIVATE plans (latest revision per slug), for the org page. */
export const listOrgCustomPlans = createServerFn({ method: 'GET' })
  .validator((d: { organizationId: string }) =>
    parseInput(z.object({ organizationId: zId }), d),
  )
  .handler(async ({ data }) => {
    await requirePlatformAdmin()
    const rows = await db
      .select({
        slug: pricingPlans.slug,
        name: pricingPlans.name,
        includedGb: pricingPlans.includedGb,
        priceMonthlyMicroUsd: pricingPlans.priceMonthlyMicroUsd,
        topUpCapGb: pricingPlans.topUpCapGb,
        effectiveAt: pricingPlans.effectiveAt,
      })
      .from(pricingPlans)
      .where(eq(pricingPlans.organizationId, data.organizationId))
      .orderBy(desc(pricingPlans.effectiveAt))
    // Latest revision per slug wins; older revisions are the audit trail.
    const seen = new Set<string>()
    const out: ((typeof rows)[number] & { perGbMicroUsd: number })[] = []
    for (const r of rows) {
      if (seen.has(r.slug)) continue
      seen.add(r.slug)
      out.push({ ...r, perGbMicroUsd: planPerGbMicroUsd(r) })
    }
    return out.map((r) => ({ ...r, effectiveAt: r.effectiveAt.toISOString() }))
  })

/**
 * Delete a private plan (every revision of the slug). Refused while anything
 * still points at it: the org's current plan, an active prepaid term, or a
 * non-void invoice — deleting under those would break plan resolution at the
 * next grant. Paid history is unaffected: invoice line items and amounts are
 * frozen at issue, and refund pricing reads the rate stamped on the ledger
 * row, not the catalog.
 */
export const deleteOrgCustomPlan = createServerFn({ method: 'POST' })
  .validator((d: { organizationId: string; slug: string }) => {
    if (!/^[a-z0-9-]{1,32}$/.test(d.slug)) {
      throw new PublicError('Invalid plan', 400)
    }
    parseInput(z.object({ organizationId: zId }), d)
    return { organizationId: d.organizationId, slug: d.slug }
  })
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    const [billing] = await db
      .select({ planSlug: organizationBilling.planSlug })
      .from(organizationBilling)
      .where(eq(organizationBilling.organizationId, data.organizationId))
      .limit(1)
    if (billing?.planSlug === data.slug) {
      throw new PublicError(
        "This is the organization's current plan. Move them off it first.",
        400,
      )
    }
    const [term] = await db
      .select({ planSlug: prepaidTerms.planSlug })
      .from(prepaidTerms)
      .where(
        and(
          eq(prepaidTerms.organizationId, data.organizationId),
          eq(prepaidTerms.active, true),
        ),
      )
      .limit(1)
    if (term?.planSlug === data.slug) {
      throw new PublicError(
        'An active prepaid term uses this plan. Let it end or change it first.',
        400,
      )
    }
    const [openInvoice] = await db
      .select({ invoiceNumber: invoices.invoiceNumber })
      .from(invoices)
      .where(
        and(
          eq(invoices.organizationId, data.organizationId),
          eq(invoices.planSlug, data.slug),
          isNull(invoices.voidedAt),
          isNull(invoices.paidAt),
        ),
      )
      .limit(1)
    if (openInvoice) {
      throw new PublicError(
        `Open invoice ${openInvoice.invoiceNumber} grants this plan. Void or collect it first.`,
        400,
      )
    }
    const deleted = await db
      .delete(pricingPlans)
      .where(
        and(
          eq(pricingPlans.organizationId, data.organizationId),
          eq(pricingPlans.slug, data.slug),
        ),
      )
      .returning({ id: pricingPlans.id })
    if (deleted.length === 0) {
      throw new PublicError('No such custom plan for this organization', 404)
    }
    emitBusinessEvent('plan.custom_deleted', {
      org_id: data.organizationId,
      plan_slug: data.slug,
      admin_id: caller.userId,
    })
    return { deleted: deleted.length }
  })

export const createOrgCustomPlan = createServerFn({ method: 'POST' })
  .validator(
    (d: {
      organizationId: string
      slug: string
      name: string
      includedGb: number
      priceMonthlyUsd: number
      topUpCapGb: number
      effectiveAt?: string
    }) => {
      if (!/^[a-z0-9-]{3,32}$/.test(d.slug)) {
        throw new PublicError(
          'Slug must be 3 to 32 lowercase letters, digits or dashes',
          400,
        )
      }
      const includedGb = Math.floor(Number(d.includedGb))
      const topUpCapGb = Math.floor(Number(d.topUpCapGb))
      const priceMonthlyMicroUsd = Math.round(Number(d.priceMonthlyUsd) * 1e6)
      if (!Number.isFinite(includedGb) || includedGb <= 0) {
        throw new PublicError(
          'Included GB must be a positive whole number',
          400,
        )
      }
      if (!Number.isFinite(priceMonthlyMicroUsd) || priceMonthlyMicroUsd <= 0) {
        throw new PublicError('Monthly price must be positive', 400)
      }
      if (!Number.isFinite(topUpCapGb) || topUpCapGb < 0) {
        throw new PublicError('Additional-data cap must not be negative', 400)
      }
      // Enforced at CREATION so the failure lands on the operator, not on the
      // customer at checkout: microUsdToCents throws on a fractional cent and
      // planPerGbMicroUsd rounds, so a pair whose implied per-GB rate is not a
      // whole number of cents renders fine but cannot be charged.
      if (!isWholeCentPlan({ includedGb, priceMonthlyMicroUsd })) {
        throw new PublicError(
          `$${Number(d.priceMonthlyUsd).toFixed(2)} over ${includedGb} GB does not divide into a whole number of cents per GB. Adjust the included GB (or the price) so it divides exactly.`,
          400,
        )
      }
      const { organizationId, name } = parseInput(
        z.object({ organizationId: zId, name: requiredText(80) }),
        d,
      )
      return {
        organizationId,
        slug: d.slug,
        name,
        includedGb,
        priceMonthlyMicroUsd,
        topUpCapGb,
        effectiveAt: d.effectiveAt ? new Date(d.effectiveAt) : new Date(),
      }
    },
  )
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    if (!data.name) throw new PublicError('Plan name is required', 400)
    const inserted = await db
      .insert(pricingPlans)
      .values({
        organizationId: data.organizationId,
        slug: data.slug,
        name: data.name,
        includedGb: data.includedGb,
        priceMonthlyMicroUsd: data.priceMonthlyMicroUsd,
        topUpCapGb: data.topUpCapGb,
        effectiveAt: data.effectiveAt,
        sortOrder: 99,
      })
      .onConflictDoNothing()
      .returning({ id: pricingPlans.id })
    if (inserted.length > 0) {
      emitBusinessEvent('plan.custom_created', {
        org_id: data.organizationId,
        plan_slug: data.slug,
        included_gb: data.includedGb,
        amount_micro_usd: data.priceMonthlyMicroUsd,
        actor: 'admin',
        admin_id: caller.userId,
      })
    }
    return {
      slug: data.slug,
      perGbMicroUsd: planPerGbMicroUsd({
        includedGb: data.includedGb,
        priceMonthlyMicroUsd: data.priceMonthlyMicroUsd,
      }),
    }
  })

/** One row of the admin email log, bodies EXCLUDED (fetched per-view). */
export interface AdminEmailLogRow {
  id: string
  type: string
  recipient: string
  subject: string
  outcome: string
  error: string | null
  organizationId: string | null
  organizationName: string | null
  createdAt: string
}

/** Rows the Emails page loads at once; older history stays queryable by id. */
const EMAIL_LOG_LIMIT = 1000

/**
 * The most recent transactional email attempts, newest first, without the
 * stored bodies (a page of full HTML bodies would be megabytes for no reason —
 * `getEmailLogEntry` fetches one on view).
 */
export const listEmailLog = createServerFn({ method: 'GET' }).handler(
  async (): Promise<AdminEmailLogRow[]> => {
    await requirePlatformAdmin()
    const rows = await db
      .select({
        id: emailLog.id,
        type: emailLog.type,
        recipient: emailLog.recipient,
        subject: emailLog.subject,
        outcome: emailLog.outcome,
        error: emailLog.error,
        organizationId: emailLog.organizationId,
        organizationName: organization.name,
        createdAt: emailLog.createdAt,
      })
      .from(emailLog)
      .leftJoin(organization, eq(organization.id, emailLog.organizationId))
      .orderBy(desc(emailLog.createdAt))
      .limit(EMAIL_LOG_LIMIT)
    return rows.map((r) => ({ ...r, createdAt: r.createdAt.toISOString() }))
  },
)

/** One logged email with its exact stored bodies, for the viewer dialog. */
export const getEmailLogEntry = createServerFn({ method: 'GET' })
  .validator((d: { id: string }) => parseInput(z.object({ id: zId }), d))
  .handler(async ({ data }) => {
    await requirePlatformAdmin()
    const [row] = await db
      .select()
      .from(emailLog)
      .where(eq(emailLog.id, data.id))
      .limit(1)
    if (!row) throw new PublicError('Email not found', 404)
    return {
      subject: row.subject,
      text: row.text,
      html: row.html,
      createdAt: row.createdAt.toISOString(),
    }
  })
